/**
 * Service for fetching apartment availability data from external booking system
 */

// Base URL for the booking system API
const API_BASE_URL = import.meta.env.VITE_BOOKING_API_URL || 'https://api.your-booking-system.com';

/**
 * Fetches availability data for a specific apartment
 * @param {string|number} apartmentId - The ID of the apartment
 * @param {Date} startDate - Optional start date for the range (defaults to current date)
 * @param {Date} endDate - Optional end date for the range (defaults to 3 months from start)
 * @returns {Promise<Array>} - Array of booked date ranges
 */
export const fetchApartmentAvailability = async (apartmentId, startDate = new Date(), endDate = null) => {
  try {
    // If no end date provided, default to 3 months from start date
    if (!endDate) {
      endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 3);
    }
    
    // Format dates for API
    const formattedStartDate = formatDateForAPI(startDate);
    const formattedEndDate = formatDateForAPI(endDate);
    
    // Construct API URL
    const url = `${API_BASE_URL}/apartments/${apartmentId}/availability?start=${formattedStartDate}&end=${formattedEndDate}`;
    
    // Make API request
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getApiToken()}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Process the data to convert string dates to Date objects
    return processAvailabilityData(data);
  } catch (error) {
    console.error('Error fetching availability data:', error);
    
    // For development/testing, return mock data when API is not available
    if (import.meta.env.DEV) {
      return getMockAvailabilityData(apartmentId);
    }
    
    throw error;
  }
};

/**
 * Helper function to format dates for the API
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string (YYYY-MM-DD)
 */
const formatDateForAPI = (date) => {
  return date.toISOString().split('T')[0];
};

/**
 * Helper function to get API token from storage or environment
 * @returns {string} - API token
 */
const getApiToken = () => {
  // In a real application, this might come from localStorage, context, or environment variables
  return import.meta.env.VITE_BOOKING_API_TOKEN || 'development-token';
};

/**
 * Process raw availability data from API
 * @param {Object} data - Raw data from API
 * @returns {Array} - Processed availability data
 */
const processAvailabilityData = (data) => {
  // Assuming the API returns data in the format:
  // { bookedRanges: [{ start: "2025-09-15", end: "2025-09-20" }, ...] }
  
  if (!data || !data.bookedRanges || !Array.isArray(data.bookedRanges)) {
    return [];
  }
  
  return data.bookedRanges.map(range => ({
    start: new Date(range.start),
    end: new Date(range.end)
  }));
};

/**
 * Generate mock availability data for development/testing
 * @param {string|number} apartmentId - The ID of the apartment
 * @returns {Array} - Mock booked date ranges
 */
const getMockAvailabilityData = (apartmentId) => {
  // Generate different mock data based on apartment ID to simulate different availability
  const currentDate = new Date();
  const nextMonth = new Date(currentDate);
  nextMonth.setMonth(currentDate.getMonth() + 1);
  
  // Use the apartment ID to create deterministic but different mock data
  const idNum = parseInt(apartmentId, 10);
  const offset = idNum % 10;
  
  const mockData = [
    // Some dates in current month
    {
      start: new Date(currentDate.getFullYear(), currentDate.getMonth(), 5 + offset),
      end: new Date(currentDate.getFullYear(), currentDate.getMonth(), 10 + offset)
    },
    // Some dates in next month
    {
      start: new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 15 + offset),
      end: new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 20 + offset)
    }
  ];
  
  // Add a third booking for even-numbered apartments
  if (idNum % 2 === 0) {
    mockData.push({
      start: new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 25),
      end: new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 5)
    });
  }
  
  return mockData;
};

export default { fetchApartmentAvailability };
